'use strict';

import gsap from "gsap";
import {isMobile, isWeb, projectMode, settingsData, UIRoot} from '../../../settings';
import {Button} from "../buttons/Button";
import {
  clamp,
  freezingCanvas,
  highlightMenuButtonDependsOnMode,
  isMapOpened,
  isPortraitMode,
  lockScreenOrientation, zoomOut, zoomToApartment
} from "../../../functions";
import {changeLangToCurrent} from "../../components/langSwitcher/langSwitcher";
import {  getActiveMenuData, toggleMenuWidth } from '../../menu/menu';
import {ViewMode} from "../../../common";
import { renderUpdateValuesOnDemand } from '../../../app';
import { generateUUID } from 'three/src/math/MathUtils';
import { BaseElement } from '../BaseElement';
import { VideoPopUp } from "../../components/VideoPopUp";
import {EventBus} from "../EventBus";
import Hammer from "hammerjs";
import {IntersectionLegenda} from "../../components/IntersectionLegenda";

export class PopUpPage {
  static #popUpPageAnimationDelay = 0;
  static #popUpPageAnimationDuration = 0;
  static #contentAnimationDelay = 0.2;
  static #globalAnimationDelay = isMobile ? 0 : 0.3;

  #element = null;
  #header = null;
  #content = null;
  #contentBody = null;
  #headerTitleIcon = null;
  #closeButton = null;

  #dragStarted;
  #panDirection;
  // #currentHeight;

  constructor(title) {
    this.#element = document.createElement('div');
    this.#content = document.createElement('div');
    this.#contentBody = document.createElement('div');

    this.#element.classList.add(
      'pop-up-page'
    );
    this.#content.classList.add(
      'pop-up-page__content-wrapper',
      // isMobile && 'background-backdrop-filter'
    );
    this.#contentBody.classList.add('pop-up-page__content-body');

    this.#createHeader(title);
    if (isMobile)
      this.#addMobileGesturesListeners();

    this.#content.appendChild(this.#header);
    this.#content.appendChild(this.#contentBody);
    this.#element.appendChild(this.#content);
    UIRoot.appendChild(this.#element);
  }

  addClass(value) {
    this.#element.classList.add(value.toString());
  }

  removeClass(...value) {
    this.#element.classList.remove(...value);
  }

  #createHeader(title) {
    this.#header = document.createElement('div');
    this.#header.classList.add('pop-up-page__header');

    const headerTitle = document.createElement('div');
    headerTitle.classList.add('pop-up-page__title');
    this.#header.appendChild(headerTitle);

    this.#headerTitleIcon = document.createElement('span');
    this.#headerTitleIcon.classList.add(
      'pop-up-page__title-icon'
    );
    headerTitle.appendChild(this.#headerTitleIcon);

    const headerTitleText = document.createElement('h1')
    headerTitleText.classList.add(
      'pop-up-page__title-text',
      'language-string',
      'text',
      'pop-up-title'
    );
    headerTitleText.setAttribute('data-dictionary', title);
    headerTitleText.innerHTML = changeLangToCurrent(title);
    headerTitle.appendChild(headerTitleText);

    this.#closeButton = !isMobile && new Button()
      .addClass('pop-up-page__close-button', 'close-button-cross')
      .event('click', this.#closeHandler.bind(this))
      .appendTo(this.#header);
  }

  set titleIcon(value) {
    this.#headerTitleIcon.classList.add(value);
  }

  get contentElement() {
    return this.#contentBody;
  }

  get root() {
    return this.#element;
  }

  get closeButton() {
    return this.#closeButton;
  }

  #closeHandler() {
    projectMode.Pagination.paginate({source: 'widget-close-button'});
    PopUpPage.animation('hide');
    highlightMenuButtonDependsOnMode();
    if (settingsData.activeViewMode === ViewMode.Orbit && !isMapOpened())
      toggleMenuWidth({action: 'full-force', source: 'orbit'});
      // toggleMenuWidth({action: 'full', source: 'eventListener'});

    if (this.#element.classList.contains('widget-video'))
      this.#element.remove();
    
    VideoPopUp.updateObsticleSize();
  }

  #addMobileGesturesListeners() {
    this.#dragStarted = false;
    this.#panDirection = null;
    // this.#currentHeight = null;

    const swipe = new Hammer.Manager(this.#element);
    swipe.add(new Hammer.Pan({
      threshold: 10
    }));

    // swipe.on('panstart',(e) => {
    //   console.log(e.target.closest('.pop-up-page__content-wrapper > *'))
    //     });

    swipe.on('panup pandown', (e) => {
      if (e.target.closest('.pop-up-page__content-wrapper') &&
          (!e.target.closest('.pop-up-page__header') && !e.target.classList.contains('pop-up-page__content-wrapper')))
        return;

      // if (!this.#currentHeight)
      //   this.#currentHeight = this.#element.DOMElement.getBoundingClientRect().height;

      this.#panDirection = e.additionalEvent;

      this.#dragStarted = true;

      if (e.isFinal) {
        this.#panHandler();
      }
    });

    swipe.on('panend', (e) => {
      if (e.target.closest('.pop-up-page__content-wrapper') &&
         (!e.target.closest('.pop-up-page__header') && !e.target.classList.contains('pop-up-page__content-wrapper')))
        return;

      if (this.#dragStarted && this.#panDirection)
        this.#panHandler();
    });
  }

  #panHandler() {
    switch (this.#panDirection) {
      case 'panup':

        break;
      case 'pandown':
        this.#closeHandler();
        break;
    }

    this.#dragStarted = false;
    this.#panDirection = null;
    // this.#currentHeight = null;
    // this.#element.event('transitionend', () => {
    //
    // }, {once: true});
  };

  static hide() {
    const allPopUpPages = document.body.querySelectorAll('.pop-up-page:not(.hidden)');
    for (let page of allPopUpPages) {
      page.classList.add('hidden');

      const isWebFilterPopUp = isWeb && page.classList.contains('filter-pop-up-page');
      isWebFilterPopUp && EventBus.trigger('shrinkSearchPutMainElementBack');
    }
  }

  static animation(action) {
    const page = document.querySelector('.pop-up-page:not(.hidden)');
    if (!page)
      return;

    const isDisplayOptionPopup = page.classList.contains('display-options-page');
    const content = page.querySelector('.pop-up-page__content-wrapper');
    switch(action) {
      case 'show':
        gsap.fromTo(
            page,
          {y: '100%'},
          {
            delay: 0,
            y: 0,
            duration: settingsData.animationsDuration,
            onStart: () => {
              if (isMobile) {
                const pwa = document.querySelector('.pwa');
                pwa && pwa.classList.add('hidden');
              }
            },
            onComplete: () => {
              page.style.removeProperty('transform');
              !isDisplayOptionPopup && renderUpdateValuesOnDemand(true);
              page.classList.add('background-backdrop-filter');
            }
          },
        );


        if (!isMobile) {
          gsap.fromTo(
              content,
              {opacity: 0},
              {
                opacity: 1,
                delay: 0,
                duration: settingsData.animationsDuration + this.#popUpPageAnimationDuration,
                onComplete: () => {
                  content.style.removeProperty('opacity');
                }
              }
          );
        }
        break;
      case 'hide':
        gsap.to(
            page,
            {
              y: '100%',
              delay: 0.2,
              duration: settingsData.animationsDuration,
              onStart: () => {
                page.classList.remove('background-backdrop-filter');
              },
              onComplete: () => {
                page.style.removeProperty('transform');
                content.style.removeProperty('opacity');
                PopUpPage.hide();
                lockScreenOrientation();
                if (isDisplayOptionPopup) {
                  renderUpdateValuesOnDemand(false);
                } else {
                  const activeMenu = getActiveMenuData();
                  if (activeMenu === '3dModel') {
                    renderUpdateValuesOnDemand(false);
                  } else if (!isMobile) {
                    renderUpdateValuesOnDemand(true);
                  }
                }

                if (isMobile && settingsData.activeViewMode === ViewMode.Orbit) {
                  const pwa = document.querySelector('.pwa');
                  pwa && pwa.classList.remove('hidden');
                }
              }
            },
        );

        if (!isMobile) {
          gsap.to(
              content,
              {
                opacity: 0,
                duration: settingsData.animationsDuration
              }
          );
        }

        break;
      default:
        console.log('PopUp: animation: default case');
        break;
    }
  }
}


const DEFAULT_PARAMS = {
  parent: document.querySelector('#app'),
  needsCloseBtn: true
};


export class PopUp {
  
  static scopes = {};
  
  constructor (params) {
    this.params = params;
    this.parent = params.parent ? params.parent : DEFAULT_PARAMS.parent;
    this.id = params.id ? params.id : generateUUID();
    this.container = new BaseElement()
        .addClass('pop-up','hidden')
        .addAttribute('id',this.id)
        .appendTo(this.parent);
    this.content = new BaseElement()
        .addClass('pop-up__content')
        .appendTo(this.container.DOMElement)
        .DOMElement;
    this.header = new BaseElement()
        .addClass('pop-up__header')
        .appendTo(this.content)
        .DOMElement;
    this.title = new BaseElement('h2')
        .addClass('pop-up__title','language-string')
        .appendTo(this.header);
    
    this.onClose = () => this.hide();
    
    if (this.params.hasOwnProperty('needsCloseBtn')) {
      if (this.params.needsCloseBtn) {
        this.addCloseBtn();
      }
    } else {
      this.addCloseBtn();
    }
    
    PopUp.scopes[this.id] = this;
  }
  
  addCloseBtn(parent) {
    new BaseElement('button')
        .addClass('pop-up__close-btn','icon-close')
        .appendTo(parent ? parent : this.header)
        .event('click',() => this.onClose());
  }
  
  setTitle(title,langKey) {
    this.title.innerHtml(title);
    if (langKey) {
      this.title.addAttribute('data-dictionary', langKey);
      this.title.innerHtml(changeLangToCurrent(langKey));
    }
  }
  
  setContent(content) {
    this.content.appendChild(content);
  }
  
  show() {
    return new Promise(resolve => {
      gsap.fromTo(this.container.DOMElement,{
        transform: 'translateY(100%)',
        opacity: 0
      },{
        transform: 'translateY(0)',
        opacity: 1,
        ease: 'Power2.easeInOut',
        onStart: () => this.container.removeClass('hidden'),
        onComplete: () => {
          this.container.removeStyle('transform');
          resolve();
        }
      })
    })
  }
  
  hide() {
    gsap.fromTo(this.container.DOMElement,{
      transform: 'translateY(0)',
      opacity: 1
    },{
      transform: 'translateY(100%)',
      opacity: 0,
      ease: 'Power2.easeInOut',
      onComplete: () => {
        this.container.addClass('hidden');
        this.container.removeStyle('transform');
        if (this.params.destroy) {
          this.destroy();
        }
      }
    })
  }
  
  destroy() {
    this.container.remove();
    delete PopUp.scopes[this.id];
  }
  
  static getScope(id) {
    return PopUp.scopes[id];
  }
  
}
